import { zodResolver } from '@hookform/resolvers/zod';
import {
  AlertCircle,
  Eye,
  EyeOff,
  Lock,
  Mail,
  ScrollText,
  ShieldCheck,
  ToggleLeft,
} from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

/*
 * Inter, for this page only.
 *
 * The product's Space Grotesk is a display face, which is right for a shell someone
 * spends a working day inside and wrong for a sign-in form, where the type has to
 * disappear so the page reads as infrastructure rather than as a landing page. The
 * switch is made by `--font-sans` inside `.auth-shell` (styles/index.css); this
 * import is only what puts the faces on the page.
 *
 * Bundled through @fontsource rather than fetched, on the same air-gap constraint as
 * the rest of the type: a tenant inside a government or defence network has no route
 * to a font CDN, and the sign-in screen is the one page that must render for them.
 * Imported here rather than in main.tsx so the dependency sits with its only
 * consumer — and so it splits with the route if this page is ever lazy-loaded.
 */
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AuthIllustration } from '@/features/auth/components/auth-illustration';
import { SsoProviders } from '@/features/auth/components/sso-providers';
import { loginSchema, type LoginInput } from '@/features/auth/schemas';
import { ApiError } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { setRememberSession, useAuthStore } from '@/stores/auth-store';
import { useBrandStore } from '@/stores/brand-store';

/*
 * Capability claims for the brand panel.
 *
 * Static and product-level — never tenant data. This panel renders before
 * authentication, so anything drawn from the organisation's own records would be
 * disclosing it to an anonymous caller who merely guessed a tenant slug.
 */
const HIGHLIGHTS = [
  { icon: ShieldCheck, label: 'Role-based access' },
  { icon: ToggleLeft, label: 'Configurable modules' },
  { icon: ScrollText, label: 'Full audit trail' },
] as const;

/*
 * The entrance, in three staged CSS animations rather than in Framer Motion.
 *
 * Framer would express this more readably, and it is what the rest of the product
 * uses — but only behind authentication. `manualChunks` in vite.config.ts keeps the
 * animation runtime in its own chunk precisely so the sign-in page never pays for
 * it, and importing it here put 41 kB gzipped into the entry's modulepreload: a
 * measurable delay on the one page every user loads first, and on the slow links a
 * government tenant is often on, to fade a card in by 12 pixels.
 *
 * The keyframes below already exist in the theme (`animate-fade-up`,
 * `animate-fade-in`), carry the product's own `--ease-out`, and are clamped to a
 * single frame by the global `prefers-reduced-motion` rule in styles/index.css — so
 * this is also the version that honours the OS setting without a hook.
 *
 * Durations are pinned to 280ms because the theme's `fade-up` runs at 320ms.
 */
const ENTER_CARD = 'animate-fade-up [animation-duration:280ms]';
const ENTER_FORM = 'animate-fade-up [animation-duration:280ms] [animation-delay:80ms]';
const ENTER_ART = 'animate-fade-in [animation-duration:280ms] [animation-delay:120ms]';

/*
 * The field geometry, in one place.
 *
 * 52px tall on a 14px radius, level with the card rather than inset — the sign-in
 * form is four controls on a lot of white, and the inset field treatment the rest of
 * the product uses is tuned for dense screens where a field has to separate itself
 * from its neighbours. Here there are no neighbours.
 */
const FIELD =
  'h-[3.25rem] rounded-[0.875rem] text-[0.9375rem] focus-visible:ring-4 focus-visible:ring-primary/15';

/** A form label at the page's own type scale, not the shell's uppercase meta style. */
function FieldLabel({ htmlFor, children }: { htmlFor: string; children: ReactNode }) {
  return (
    <Label
      htmlFor={htmlFor}
      className="font-sans text-[0.875rem] font-medium normal-case leading-5 tracking-normal text-foreground"
    >
      {children}
    </Label>
  );
}

function FieldError({ id, children }: { id: string; children: ReactNode }) {
  return (
    <p id={id} role="alert" className="flex items-center gap-1.5 text-[0.8125rem] text-destructive">
      <AlertCircle className="size-3.5 shrink-0" aria-hidden="true" />
      {children}
    </p>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuthStore((state) => state.login);
  const user = useAuthStore((state) => state.user);
  const brand = useBrandStore((state) => state.brand);

  const helpId = useId();

  const [formError, setFormError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [showResetHelp, setShowResetHelp] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  if (user) {
    const target = (location.state as { from?: Location } | null)?.from?.pathname ?? '/';
    return <Navigate to={target} replace />;
  }

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);

    // Recorded before the request, so the `set()` a successful login performs is
    // already writing the token pair into the store the reader chose.
    setRememberSession(remember);

    try {
      await login(values.email, values.password);
      navigate('/', { replace: true });
    } catch (error) {
      if (error instanceof ApiError && error.details) {
        // Server field errors land on the input they belong to, not in a toast.
        for (const [field, messages] of Object.entries(error.details)) {
          if (field in values && messages[0]) {
            setError(field as keyof LoginInput, { message: messages[0] });
          }
        }
      }
      setFormError(
        error instanceof ApiError ? error.message : 'Sign-in failed. Please try again.',
      );
    }
  });

  /*
   * Where "Forgot password?" goes.
   *
   * There is no self-service reset in this product — passwords are reissued by an
   * administrator — so the link resolves to whatever the organisation configured as
   * its support channel, and falls back to explaining the actual process rather than
   * to a route that does not exist.
   */
  const supportHref =
    brand?.support_url ||
    (brand?.support_email
      ? `mailto:${brand.support_email}?subject=${encodeURIComponent('Password reset request')}`
      : null);

  const forgotClasses =
    'focus-ring rounded-sm text-[0.8125rem] font-medium text-primary underline-offset-4 hover:underline';

  return (
    /*
     * `.auth-shell` is a token scope, not a skin: it redeclares the neutral ramp,
     * the type family and `color-scheme` for this subtree only. Because custom
     * properties inherit from the nearest declaring ancestor, it beats both the
     * dark theme and the inline palette the brand provider writes onto `:root` —
     * which is the whole of how this page stays light without an override.
     */
    /*
     * `font-sans` is not redundant with the `--font-sans` override in `.auth-shell`.
     * Tailwind's preflight declares `font-family: var(--font-sans), …` on `html`,
     * which resolves the variable *there* and inherits the resulting computed value
     * down — redeclaring the variable on a descendant changes nothing on its own.
     * The utility re-declares `font-family` at this element, which is what makes the
     * scoped value take effect.
     */
    <div className="auth-shell relative flex min-h-dvh w-full items-center justify-center overflow-x-hidden bg-background px-4 py-8 font-sans sm:px-6 sm:py-12">
      {/* Page texture, in two layers: a hairline honeycomb at 4%, then the bloom the
          card sits in. Both inert and hidden from assistive technology. */}
      <div className="bg-hex pointer-events-none absolute inset-0 opacity-[0.04]" aria-hidden="true" />
      <div className="bg-auth-bloom pointer-events-none absolute inset-0" aria-hidden="true" />

      <main className={cn('relative z-10 w-full max-w-[75rem]', ENTER_CARD)}>
        <div className="grid overflow-hidden rounded-[1.75rem] border border-primary/[0.08] bg-card shadow-[var(--shadow-auth-card)] lg:min-h-[43.75rem] lg:grid-cols-[55fr_45fr]">
          {/* ---- Visual panel ------------------------------------------------
              A 180/220px band above the form until `lg`, where it becomes the left
              55%. Stacked rather than hidden on a phone: it is the only thing on the
              page carrying the organisation's identity before sign-in, and the form
              below it still lands within a thumb's reach. */}
          <div className="relative flex h-[11.25rem] flex-col overflow-hidden bg-gradient-to-br from-secondary/50 via-secondary/25 to-secondary/45 sm:h-[13.75rem] lg:h-auto">
            <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
              {/* Indigo at 5–7%, blurred well past its own edge so it reads as
                  light in the panel rather than as a shape on it. */}
              <span className="absolute -left-16 -top-24 size-64 rounded-full bg-primary/[0.07] blur-3xl" />
              <span className="absolute -bottom-28 -right-12 size-72 rounded-full bg-primary/[0.06] blur-3xl" />
              <span className="absolute left-1/2 top-1/3 size-40 -translate-x-1/2 rounded-full bg-primary/[0.05] blur-2xl" />
            </div>
            <div className="bg-dots pointer-events-none absolute inset-0 opacity-[0.05]" aria-hidden="true" />

            <div
              className={cn(
                'relative flex flex-1 items-center justify-center p-5 sm:p-6 lg:p-10 xl:p-12',
                ENTER_ART,
              )}
            >
              {/* Height-driven while it is a band, width-driven once it is a column:
                  in the band the 220px ceiling is the constraint, in the column the
                  panel's own width is. */}
              <AuthIllustration className="h-[9rem] w-auto sm:h-[11rem] lg:h-auto lg:w-full lg:max-w-[28rem] xl:max-w-[31rem]" />
            </div>

            {/* The caption is desktop-only: in a 220px band there is no room for it
                that does not crush the illustration it is captioning. */}
            <div className="relative hidden px-10 pb-10 lg:block xl:px-12 xl:pb-12">
              <h2 className="text-[1.625rem] font-bold leading-[1.2] tracking-[-0.02em] text-secondary-foreground">
                {brand?.organisation_name || 'Enterprise learning, configured — not coded.'}
              </h2>

              {brand?.tagline ? (
                <p className="mt-2.5 text-[0.9375rem] leading-relaxed text-primary/80">
                  {brand.tagline}
                </p>
              ) : null}

              <ul className="mt-6 flex flex-wrap gap-2">
                {HIGHLIGHTS.map(({ icon: Icon, label }) => (
                  <li
                    key={label}
                    className="inline-flex items-center gap-1.5 rounded-full border border-primary/15 bg-card/70 px-3 py-1.5 text-[0.75rem] font-medium text-primary"
                  >
                    <Icon className="size-3.5" aria-hidden="true" />
                    {label}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* ---- Form panel --------------------------------------------------- */}
          <section className="flex items-center justify-center bg-card px-6 py-10 sm:px-10 sm:py-12 lg:px-12 xl:px-14">
            <div className={cn('w-full max-w-[26.25rem]', ENTER_FORM)}>
              {/* Lockup. A tenant logo generally carries its own wordmark, so the
                  product name is set beside the mark only when there is no logo to
                  read it from. */}
              {brand?.logo_url ? (
                <img
                  src={brand.logo_url}
                  alt={brand.organisation_name}
                  className="h-10 w-auto object-contain"
                  width={160}
                  height={40}
                />
              ) : (
                <div className="flex items-center gap-3">
                  <span
                    className="flex size-10 items-center justify-center rounded-[0.75rem] bg-primary text-[0.9375rem] font-bold text-primary-foreground"
                    aria-hidden="true"
                  >
                    {(brand?.organisation_name || brand?.product_name || 'L').charAt(0)}
                  </span>
                  <span className="text-[1.0625rem] font-semibold tracking-[-0.01em] text-foreground">
                    {brand?.product_name || 'Learning Platform'}
                  </span>
                </div>
              )}

              <span className="mt-8 inline-flex items-center gap-2 rounded-full border border-primary/15 bg-primary/[0.06] px-3 py-1 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-primary">
                <span className="size-1.5 rounded-full bg-primary" aria-hidden="true" />
                Welcome back
              </span>

              <h1 className="mt-4 text-[2rem] font-bold leading-[1.15] tracking-[-0.025em] text-foreground">
                Sign in to your account
              </h1>
              <p className="mt-2.5 text-[0.9375rem] leading-relaxed text-primary/80">
                Access your dashboard, training, jobs and notifications.
              </p>

              {brand?.login_message ? (
                <p className="mt-6 flex items-start gap-2.5 rounded-[0.875rem] border border-border bg-background p-3.5 text-[0.8125rem] leading-relaxed text-muted-foreground">
                  <Lock className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                  {brand.login_message}
                </p>
              ) : null}

              <form onSubmit={onSubmit} noValidate className="mt-8 space-y-5">
                <div className="space-y-2">
                  <FieldLabel htmlFor="email">Email address</FieldLabel>
                  <Input
                    id="email"
                    type="email"
                    inputMode="email"
                    autoComplete="username"
                    autoFocus
                    placeholder="you@organisation.gov"
                    className={FIELD}
                    startIcon={<Mail />}
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={errors.email ? 'email-error' : undefined}
                    {...register('email')}
                  />
                  {errors.email ? (
                    <FieldError id="email-error">{errors.email.message}</FieldError>
                  ) : null}
                </div>

                <div className="space-y-2">
                  <FieldLabel htmlFor="password">Password</FieldLabel>
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    className={cn(FIELD, 'pr-12')}
                    startIcon={<Lock />}
                    aria-invalid={Boolean(errors.password)}
                    aria-describedby={errors.password ? 'password-error' : undefined}
                    endIcon={
                      <button
                        type="button"
                        onClick={() => setShowPassword((visible) => !visible)}
                        // Not a toggle button by role: its label changes to describe
                        // the next action, which is clearer here than a pressed state.
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        className="focus-ring touch-target relative flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors duration-hover ease-control hover:text-foreground"
                      >
                        {showPassword ? (
                          <EyeOff className="size-4" aria-hidden="true" />
                        ) : (
                          <Eye className="size-4" aria-hidden="true" />
                        )}
                      </button>
                    }
                    {...register('password')}
                  />
                  {errors.password ? (
                    <FieldError id="password-error">{errors.password.message}</FieldError>
                  ) : null}
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3">
                  <label className="inline-flex cursor-pointer select-none items-center gap-2.5 text-[0.8125rem] font-medium text-muted-foreground">
                    <Checkbox
                      checked={remember}
                      onChange={(event) => setRemember(event.target.checked)}
                    />
                    Remember me
                  </label>

                  {supportHref ? (
                    <a href={supportHref} rel="noopener" className={forgotClasses}>
                      Forgot password?
                    </a>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowResetHelp((open) => !open)}
                      aria-expanded={showResetHelp}
                      aria-controls={helpId}
                      className={forgotClasses}
                    >
                      Forgot password?
                    </button>
                  )}
                </div>

                {showResetHelp && !supportHref ? (
                  <p
                    id={helpId}
                    className="animate-fade-in rounded-[0.875rem] border border-border bg-background p-3.5 text-[0.8125rem] leading-relaxed text-muted-foreground"
                  >
                    Passwords are reissued by your organisation&rsquo;s administrator. Contact them
                    to have yours reset — this platform does not email reset links.
                  </p>
                ) : null}

                {formError ? (
                  <p
                    role="alert"
                    className="flex animate-fade-in items-start gap-2.5 rounded-[0.875rem] border border-destructive/25 bg-destructive/[0.06] p-3.5 text-[0.8125rem] leading-relaxed text-destructive"
                  >
                    <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                    {formError}
                  </p>
                ) : null}

                <Button
                  type="submit"
                  fullWidth
                  loading={isSubmitting}
                  loadingText="Signing in…"
                  /*
                   * The page's own CTA geometry — 52px on a 14px radius — and its own
                   * hover direction. The shell's primary darkens under the pointer
                   * because it sits on the page; on white it has to lighten, so the
                   * hover reads as the surface coming forward rather than as the
                   * control being pressed. `--auth-primary-hover` derives that step
                   * from `--primary`, so a white-labelled action colour gets its own.
                   */
                  className="mt-1 h-[3.25rem] rounded-[0.875rem] text-[1rem] hover:bg-[var(--auth-primary-hover)]"
                >
                  Sign In
                </Button>
              </form>

              {/* Renders only for organisations with providers configured — see
                  SsoProviders. Nothing here is a placeholder for an integration
                  that has not been bought. */}
              <div className="mt-7">
                <SsoProviders providers={brand?.sso_providers ?? []} />
              </div>
            </div>
          </section>
        </div>

        <p className="mt-6 text-center text-[0.75rem] text-muted-foreground">
          Protected by role-based access control and a full audit trail.
        </p>
      </main>
    </div>
  );
}
